import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import {
  GridPaginationModel,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useCallback, useMemo } from "react";

import { AutocompleteSelect } from "./AutocompleteSelect";
import { Button } from "./ui/Button";

const FALLBACK_PAGE_SIZES = [10, 25, 50, 100];
const PAGE_BUTTON_BUDGET = 5;


function optionValue(option: number | { value: number; label: string }): number {
  return typeof option === "number" ? option : option.value;
}

function optionLabel(option: number | { value: number; label: string }): string {
  return typeof option === "number" ? String(option) : option.label;
}

function recordCopy(count: number): string {
  return `${count} ${count === 1 ? "record" : "records"}`;
}

/**
 * Compact page list: always include first and last, keep the current page in
 * the middle, and collapse the rest to an ellipsis. Five numbered slots is
 * enough for the footer on 390px without wrapping onto a second toolbar.
 */
export function visiblePages(
  page: number,
  lastPage: number,
  budget = PAGE_BUTTON_BUDGET
): Array<number | "ellipsis"> {
  if (lastPage < 0) return [];
  if (lastPage + 1 <= budget) {
    return Array.from({ length: lastPage + 1 }, (_, index) => index);
  }

  const windowSize = Math.max(1, budget - 2);
  let start = Math.min(
    Math.max(1, page - Math.floor((windowSize - 1) / 2)),
    lastPage - windowSize
  );
  start = Math.max(1, start);
  const end = Math.min(lastPage - 1, start + windowSize - 1);

  const items: Array<number | "ellipsis"> = [0];
  if (start > 1) items.push("ellipsis");
  for (let index = start; index <= end; index += 1) items.push(index);
  if (end < lastPage - 1) items.push("ellipsis");
  items.push(lastPage);
  return items;
}

/**
 * DataGrid pagination slot. Reads the grid's pagination model and writes back
 * through `onPaginationModelChange` / `setPaginationModel` so the table stays
 * the single source of truth.
 *
 * Page-size stays visible even when the current page holds every row — otherwise
 * a 50-row table can never switch to 10 or 25. First/last and numbered pages
 * sit next to prev/next so a 290-row demo is not a click-train.
 */
export function DataTablePagination() {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const theme = useTheme();
  const desktopPagination = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);

  const page = paginationModel?.page ?? 0;
  const pageSize = paginationModel?.pageSize ?? 50;
  const count = Number.isFinite(rowCount) ? Math.max(0, rowCount) : 0;
  const lastPage = pageSize > 0 ? Math.max(0, Math.ceil(count / pageSize) - 1) : 0;
  const pageCount = count === 0 ? 0 : lastPage + 1;
  const onePage = pageCount <= 1;
  const from = count === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(count, (page + 1) * pageSize);

  const pages = useMemo(
    () => (onePage ? [] : visiblePages(page, lastPage)),
    [onePage, page, lastPage]
  );

  const pageSizeOptions = (rootProps.pageSizeOptions?.length
    ? rootProps.pageSizeOptions
    : FALLBACK_PAGE_SIZES) as ReadonlyArray<number | { value: number; label: string }>;

  const pageSizeChoices = useMemo(
    () =>
      pageSizeOptions.map((option) => ({
        label: `${optionLabel(option)} / page`,
        value: String(optionValue(option)),
      })),
    [pageSizeOptions]
  );

  const setModel = useCallback(
    (next: GridPaginationModel) => {
      if (rootProps.onPaginationModelChange) {
        rootProps.onPaginationModelChange(next, { api: apiRef.current });
      } else {
        apiRef.current.setPaginationModel(next);
      }
    },
    [apiRef, rootProps]
  );

  const goTo = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(0, Math.min(lastPage, nextPage));
      if (clamped === page) return;
      setModel({ page: clamped, pageSize });
    },
    [lastPage, page, pageSize, setModel]
  );

  return (
    <nav
      className="ml-auto flex flex-wrap items-center justify-end gap-1 px-2 py-0.5 text-xs text-od-muted sm:gap-2 sm:px-3 sm:py-1.5"
      aria-label="Table pagination"
    >
      <span className="tabular-nums">
        {onePage ? recordCopy(count) : `${from}–${to} of ${count}`}
      </span>
      <label className="inline-flex items-center gap-1.5">
        <span className="sr-only">Rows per page</span>
        <div className="w-32 shrink-0">
          <AutocompleteSelect
            compact
            disableClearable
            ariaLabel="Rows per page"
            options={pageSizeChoices}
            value={String(pageSize)}
            onChange={(value) => {
              // Defensive: the control is not clearable, so an empty pick
              // should never arrive, and must not zero the page size if it does.
              if (typeof value === "string" && value) {
                setModel({ page: 0, pageSize: Number(value) });
              }
            }}
          />
        </div>
      </label>
      {!onePage && (
        <>
          <Button
            variant="secondary" size="icon"
            aria-label="First page"
            disabled={page <= 0}
            onClick={() => goTo(0)}
          >
            <FirstPageIcon sx={{ fontSize: 16 }} />
          </Button>
          <Button
            variant="secondary" size="icon"
            aria-label="Previous page"
            disabled={page <= 0}
            onClick={() => goTo(page - 1)}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </Button>
          {desktopPagination ? (
            <div className="flex items-center gap-1">
              {pages.map((item, index) =>
                item === "ellipsis" ? (
                  <span
                    key={`e-${index}`}
                    className="px-1 text-od-muted"
                    aria-hidden
                  >
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    variant={item === page ? "primary" : "secondary"} size="icon" className="w-auto min-w-11 px-2 tabular-nums sm:w-auto sm:min-w-8"
                    aria-label={`Page ${item + 1}`}
                    aria-current={item === page ? "page" : undefined}
                    onClick={() => goTo(item)}
                  >
                    {item + 1}
                  </Button>
                )
              )}
            </div>
          ) : (
            <span className="min-w-[3.5rem] text-center tabular-nums text-od-text">
              {page + 1} / {pageCount}
            </span>
          )}
          <Button
            variant="secondary" size="icon"
            aria-label="Next page"
            disabled={page >= lastPage}
            onClick={() => goTo(page + 1)}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </Button>
          <Button
            variant="secondary" size="icon"
            aria-label="Last page"
            disabled={page >= lastPage}
            onClick={() => goTo(lastPage)}
          >
            <LastPageIcon sx={{ fontSize: 16 }} />
          </Button>
        </>
      )}
    </nav>
  );
}
